import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Tooltip, Checkbox, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, GetApp as ExportIcon, Search as SearchIcon, Clear as ClearIcon, Room as LocationIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import { fetchShipTo, searchShipTo, deleteShipTo, bulkActivateShipTo, bulkDeactivateShipTo, bulkDeleteShipTo, exportShipToCsv } from '../../store/reducers/shipToSlice';

const useStyles = makeStyles((theme) => ({ root: { padding: theme.spacing(3) }, header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(3) }, searchContainer: { display: 'flex', gap: theme.spacing(2), marginBottom: theme.spacing(3), alignItems: 'center' }, tableContainer: { marginBottom: theme.spacing(2) }, actionButtons: { display: 'flex', gap: theme.spacing(1), marginBottom: theme.spacing(2) }, statusChip: { minWidth: 80 }, loadingContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 } }));

const ShipToList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, pagination, selectedIds, bulkLoading, successMessage } = useSelector((s: RootState) => s.shipTo);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => { dispatch(fetchShipTo()); }, [dispatch]);

  const handleSearch = () => { dispatch(searchShipTo({ page: 1, pageSize: pagination.pageSize, search: searchTerm, active: activeFilter === 'all' ? undefined : activeFilter === 'true' })); };
  const handleClearSearch = () => { setSearchTerm(''); setActiveFilter('all'); dispatch(fetchShipTo({ page: 1, pageSize: pagination.pageSize })); };

  const handlePageChange = (_: unknown, newPage: number) => { dispatch(fetchShipTo({ page: newPage + 1, pageSize: pagination.pageSize, search: searchTerm || undefined, active: activeFilter === 'all' ? undefined : activeFilter === 'true' })); };
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => { dispatch(fetchShipTo({ page: 1, pageSize: parseInt(e.target.value, 10), search: searchTerm || undefined, active: activeFilter === 'all' ? undefined : activeFilter === 'true' })); };

  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      // select all ids on current page
      const ids = items.filter(i => i.Id != null).map(i => i.Id!)
      dispatch({ type: 'shipTo/setSelectedIds', payload: ids });
    } else {
      dispatch({ type: 'shipTo/clearSelectedIds' });
    }
  };

  const toggleSelect = (id: number, checked: boolean) => {
    dispatch({ type: checked ? 'shipTo/addSelectedId' : 'shipTo/removeSelectedId', payload: id });
  };

  const handleDelete = (id: number) => { if (window.confirm('Delete this Ship-To?')) dispatch(deleteShipTo(id)); };
  const handleBulkActivate = () => { dispatch(bulkActivateShipTo(selectedIds)); };
  const handleBulkDeactivate = () => { dispatch(bulkDeactivateShipTo(selectedIds)); };
  const handleBulkDelete = () => { if (selectedIds.length && window.confirm(`Delete ${selectedIds.length} item(s)?`)) dispatch(bulkDeleteShipTo(selectedIds)); };
  const handleExport = () => { dispatch(exportShipToCsv({ search: searchTerm || undefined, active: activeFilter === 'all' ? undefined : activeFilter === 'true' })); };

  if (loading && items.length === 0) return (<Box className={classes.loadingContainer}><CircularProgress /></Box>);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Ship To</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/catalogs/shipto/add')}>Add New</Button>
      </Box>
      {error && (<Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>)}
      {successMessage && (<Alert severity="success" style={{ marginBottom: 16 }}>{successMessage}</Alert>)}

      <Box className={classes.searchContainer}>
        <TextField label="Search" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} style={{ minWidth: 240 }} />
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as string)} label="Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
        <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>Clear</Button>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport} disabled={loading}>Export CSV</Button>
      </Box>

      <Box className={classes.actionButtons}>
        <Button variant="outlined" onClick={handleBulkActivate} disabled={!selectedIds.length || bulkLoading}>Activate</Button>
        <Button variant="outlined" onClick={handleBulkDeactivate} disabled={!selectedIds.length || bulkLoading}>Deactivate</Button>
        <Button variant="outlined" color="secondary" onClick={handleBulkDelete} disabled={!selectedIds.length || bulkLoading}>Delete</Button>
      </Box>

      <Paper className={classes.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox"><Checkbox indeterminate={selectedIds.length > 0 && selectedIds.length < items.length} checked={items.length > 0 && selectedIds.length === items.length} onChange={toggleSelectAll} /></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>City</TableCell>
                <TableCell>State</TableCell>
                <TableCell>ZIP</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.Id} hover>
                  <TableCell padding="checkbox"><Checkbox checked={selectedIds.includes(item.Id!)} onChange={(e) => toggleSelect(item.Id!, e.target.checked)} /></TableCell>
                  <TableCell><Box display="flex" alignItems="center"><LocationIcon style={{ marginRight: 8, fontSize: 20 }} />{item.AttName}</Box></TableCell>
                  <TableCell>{item.AttCity}</TableCell>
                  <TableCell>{item.AttState}</TableCell>
                  <TableCell>{item.AttZIP}</TableCell>
                  <TableCell><Chip label={item.Active ? 'Active' : 'Inactive'} color={item.Active ? 'primary' : 'default'} size="small" /></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => navigate(`/catalogs/shipto/edit/${item.Id}`)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="secondary" onClick={() => handleDelete(item.Id!)}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={pagination.total} rowsPerPage={pagination.pageSize} page={pagination.page - 1} onPageChange={handlePageChange} onRowsPerPageChange={handlePageSizeChange} />
      </Paper>
    </Box>
  );
};

export default ShipToList; 